import { Button } from '@/components/ui/button';
import { formatPrice, Credit } from './pricingModel';
import { UserInfoContextType, useUserInfo } from '@/provider/UserInfoProvider';
import { loadStripe } from '@stripe/stripe-js';
import { createSubscriptionEndpoint } from '@/config/endpoints';
import { app } from '@/config/firebaseConfig';
import { getAuth } from 'firebase/auth';
import { toast } from 'sonner';
import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { useSearchParams } from 'next/navigation';
import { useSignupModal } from '@/zustand/store';
import { buildUrlWithParams } from '@/utils/someUtils';
import { kazeFetch } from '@/utils/kazeFetch';
import { type EventMap, posthogUTM, RPostHog } from '@/posthog';
import { Check } from 'phosphor-react';
import type { TypePaidTier } from '../root/GlobalPricingModel/GlobalPricingModel';

interface PaidPlanCardProps {
  _from: EventMap['price_success']['pay_from_module'];
  tier: TypePaidTier;
  isYearly: boolean;
  selectedCredit: Credit;
  onCreditSelect: (credit: Credit) => void;
  onYearlyChange?: (isYearly: boolean) => void;
  className?: string;
  userIdentity?: UserInfoContextType['userIdentity'];
  userId?: string;
  cancelUrl?: string;
  successRedirect?: string;
  isModal?: boolean;
  onSelect?: () => void;
}

export function usePaymentParams() {
  const searchParams = useSearchParams();
  return {
    productName: searchParams.get('product_name'),
    creditAmount: searchParams.get('credit_amount'),
    canceled: searchParams.get('canceled') === 'true',
    payment_success: searchParams.get('payment_success') === 'true',
  };
}

const { utm } = posthogUTM();
export function PaidPlanCard({
  _from,
  tier,
  isYearly,
  selectedCredit,
  onCreditSelect,
  onYearlyChange,
  className,
  userIdentity,
  cancelUrl,
  successRedirect,
  isModal = true,
  onSelect,
}: PaidPlanCardProps) {
  const [isLoading, setIsLoading] = useState(false);
  const { updateUserInfoAssets } = useUserInfo();
  const stripePromise = loadStripe(
    process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY!
  );
  const setSignupModalOpen = useSignupModal((s) => s.setSignupModalOpen);

  const getCurrentPrice = () => {
    if (tier.name === 'Pay as You Go') {
      return formatPrice(selectedCredit?.monthlyPrice || 0);
    }
    return formatPrice(
      isYearly ? selectedCredit?.yearlyPrice : selectedCredit?.monthlyPrice
    );
  };

  const getImagePrice = () => {
    if (tier.name === 'Pay as You Go') {
      return formatPrice(selectedCredit?.pricePerImage || 0);
    }
    return formatPrice(
      isYearly
        ? selectedCredit?.yearlyPricePerImage
        : selectedCredit?.monthlyPricePerImage
    );
  };

  /**
   * @description 订阅
   * @param isSkipLogin 是否跳过验证登录
   */
  const handleSubscribe = async (isSkipLogin = false) => {
    if (!selectedCredit?.amount) {
      toast.info('Please select a credit package first');
      return;
    }

    try {
      // 上报
      if (userIdentity === 3) {
        RPostHog('user_after_click_price_btn', {
          user_identity: userIdentity,
          btn_name: tier.buttonText,
          btn_product_name: tier.name,
          ...utm,
        });
      } else {
        userIdentity &&
          RPostHog('user_not_exist_after_click_price_btn', {
            user_identity: userIdentity,
            btn_name: tier.buttonText,
            btn_product_name: tier.name,
            ...utm,
          });
      }

      if (!isSkipLogin && userIdentity !== 3) {
        setSignupModalOpen(true);
        return;
      }
      setIsLoading(true);
      const auth = getAuth(app);
      await auth.authStateReady();
      const user = auth.currentUser;
      const userId = user?.uid;

      if (!user || user.uid !== userId) {
        toast.error('User authentication failed');
        return;
      }

      const productName =
        tier.name === 'Pay as You Go'
          ? `Onetime_${Number(selectedCredit.amount)}credits`
          : `Sub_${Number(selectedCredit.amount)}credits_${isYearly ? 'yearly' : 'monthly'}`;

      const reqBody = JSON.stringify({
        product_name: productName,
        user_id: userId,
        module: _from,
        cancel_url: buildUrlWithParams(
          cancelUrl || '/pricing',
          isModal
            ? {
                canceled: 'true',
                product_name: productName,
                credit_amount: selectedCredit.amount,
              }
            : {}
        ),
        success_redirect: buildUrlWithParams(
          successRedirect || '/pricing',
          isModal
            ? {
                payment_success: 'true',
              }
            : {}
        ),
      });

      const data = await kazeFetch(createSubscriptionEndpoint, {
        method: 'POST',
        body: reqBody,
        headers: {
          utm: `product_name=${productName}`,
        },
      });

      updateUserInfoAssets();

      const { session_id } = data;
      const stripe = await stripePromise;
      await stripe!.redirectToCheckout({
        sessionId: session_id,
      });
    } catch (error: any) {
      console.error('Payment Error:', error);

      if (error?.code === 'SUB_YEAR_TO_MONTH_ERROR') {
        toast.error(
          'Annual subscribers cannot switch to monthly plans. Please continue with an annual subscription or wait until your current subscription ends.'
        );
        setIsLoading(false);
        return;
      }

      const errorMessage = error?.message || 'Payment processing failed';

      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      className={`${className || ''} rounded-xl ${
        tier.popular
          ? 'bg-gradient-to-r from-[#FD5C63] to-[#4C00FF] p-[1.5px]'
          : 'border border-gray-200'
      } cursor-pointer`}
      onClick={onSelect}
    >
      <div
        className={`h-full rounded-[11px] ${tier.popular ? 'bg-white' : 'bg-transparent'} p-6`}
      >
        <div className='relative z-10'>
          <div className='flex flex-row items-center justify-start gap-4'>
            <h3 className='text-xl font-semibold text-[#252933]'>
              {tier.name}
            </h3>
            {tier.popular && (
              <span className='font-helvetica-medium rounded-full bg-[#FD5C63] px-3 py-1.5 text-xs text-white'>
                Most popular
              </span>
            )}
          </div>

          <div className='mt-4 flex flex-row items-center justify-between'>
            <div className='flex items-baseline'>
              <span className='text-3xl font-bold text-[#252933]'>
                {getImagePrice()}
              </span>
              {(tier.priceUnit || tier.name === 'Pay as You Go') && (
                <span className='ml-1 text-xl font-medium text-gray-800'>
                  {tier.priceUnit || '/ image'}
                </span>
              )}
            </div>
            {'credits' in tier && (
              <div className='mt-2 font-medium text-gray-800'>
                {getCurrentPrice()}
                {tier.name === 'Get Pro' && <span className='ml-1'>/mo</span>}
              </div>
            )}
          </div>

          {tier.billing && (
            <div className='mt-2 flex items-center gap-2'>
              <div className='my-1 flex items-center'>
                <span
                  className={`${!isYearly ? 'text-blue-500' : 'text-[#252933]'}`}
                >
                  Monthly
                </span>
                <button
                  onClick={() => onYearlyChange?.(!isYearly)}
                  className={`relative mx-2 h-6 w-12 rounded-full transition-colors duration-200 ease-in-out ${
                    isYearly ? 'bg-blue-500' : 'bg-gray-200'
                  }`}
                >
                  <span
                    className={`absolute left-1 top-1 h-4 w-4 rounded-full bg-white transition-transform duration-200 ease-in-out ${
                      isYearly ? 'translate-x-6' : 'translate-x-0'
                    }`}
                  />
                </button>
                <span
                  className={`inline-flex items-center ${isYearly ? 'text-blue-500' : 'text-[#252933]'}`}
                >
                  Yearly <span className='ml-1 text-red-500'>(-50%)</span>
                </span>
              </div>
            </div>
          )}

          {tier.description && (
            <p className='font-helvetica-roman mb-1.5 mt-2.5 text-[16px] text-gray-600'>
              {tier.description}
            </p>
          )}

          <Button
            className={`mt-4 w-full ${
              tier.popular
                ? ''
                : 'border border-gray-200 bg-gray-50 text-gray-900 hover:bg-gray-100'
            } ${isLoading ? 'pointer-events-none' : 'pointer-events-auto'}`}
            onClick={() =>
              userIdentity === 3
                ? handleSubscribe()
                : setSignupModalOpen(true, {
                    signupSuccess: () => handleSubscribe(true),
                  })
            }
          >
            {isLoading ? (
              <Loader2 className='animate-spin' size={24} />
            ) : (
              tier.buttonText
            )}
          </Button>

          {tier.credits && (
            <div className='mt-6 grid grid-cols-3 gap-2'>
              {tier.credits.map((credit, index) => (
                <div
                  key={index}
                  className={`rounded-lg p-[1.5px] ${
                    selectedCredit?.amount === credit.amount
                      ? 'bg-gradient-to-r from-[#FD5C63] to-[#4C00FF]'
                      : 'bg-[#CBCBCB]'
                  }`}
                >
                  <button
                    onClick={() => onCreditSelect(credit)}
                    className='w-full rounded-[7px] bg-white p-2 text-center transition-colors hover:bg-gray-50'
                  >
                    <div className='font-medium'>{credit.amount}</div>
                    <div className='text-sm text-[#252933]'>{credit.label}</div>
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className='mt-6 space-y-3'>
            {tier.features.map((feature, index) => (
              <div key={index} className='flex items-center gap-2'>
                <Check size={20} weight='bold' className='text-[#6137E0]' />
                <span className='text-[16px] text-[#252933]'>{feature}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
